/**
 * Eine Stelle für „nach einer Zuordnung alles nachziehen".
 *
 * Belege-Reiter und Konto-Reiter zeigen dieselbe Zuordnung aus zwei
 * Blickrichtungen. Bis 25.09.2026 hatte jede Seite ihre eigene Liste von
 * Schlüsseln, die sie nach einer Änderung neu lädt — und die Listen waren
 * verschieden: wer im Konto-Reiter zuordnete, sah im Ausgangskorb und am
 * Lohnkonto des Helfers den alten Stand; wer im Belege-Reiter zuordnete, sah
 * die Salden alt. Beide Seiten rufen jetzt diese Funktion.
 */
import type { QueryClient } from "@tanstack/react-query";

export const FINANZ_SCHLUESSEL: readonly (readonly string[])[] = [
  ["belege"],
  ["konto"],
  ["kontobewegungen"],
  ["salden"],
  // Ein zugeordneter Beleg ist bezahlt → raus aus dem Ausgangskorb.
  ["zahllauf"],
  // Projektausgaben und Wirtschaftlichkeit hängen am Beleg.
  ["projekte"],
  ["projekt"],
  // Lohnkonto des Helfers (Helferrechnung ↔ Auszahlung).
  ["kontor", "team"],
  // Rechnungen am Lieferanten (Lieferantenblatt, Stand offen/bezahlt).
  ["lieferant"],
  // Gesellschafterkonten: ein Beleg als Privatentnahme/Auslage bucht dort.
  ["finanzen"],
  // Positionen eines Belegs gehören zum Lieferanten am Beleg (Migration 182).
  ["artikel"],
];

export function finanzenNachziehen(qc: QueryClient): void {
  for (const key of FINANZ_SCHLUESSEL) qc.invalidateQueries({ queryKey: [...key] });
}

/**
 * Wie `finanzenNachziehen`, aber OHNE die Belegliste selbst.
 *
 * Die Liste sind ~430 Belege; nach jeder Änderung eines EINZELNEN Belegs lud
 * Kontor sie bis 25.09.2026 komplett neu (gemessen: Median 4,8 s, bis 50 s,
 * 138-mal in vier Stunden). Die Antwort des Servers auf die Änderung IST der
 * neue Stand dieses Belegs — sie wird direkt in die Liste gesetzt; die übrigen
 * Belege hat die Änderung nicht berührt.
 */
export function finanzenNachziehenOhneBelegliste(qc: QueryClient): void {
  for (const key of FINANZ_SCHLUESSEL) {
    if (key[0] === "belege") {
      qc.invalidateQueries({ queryKey: ["belege"], predicate: (q) => q.queryKey[1] !== "liste" });
    } else {
      qc.invalidateQueries({ queryKey: [...key] });
    }
  }
}
